import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Quotation } from '@/types/schemas';

export async function fetchAllQuotations(): Promise<Quotation[]> {
  const snap = await getDocs(
    query(collection(db, 'quotations'), orderBy('createdAt', 'desc'), fbLimit(100)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Quotation, 'id'>) }));
}

export async function fetchQuotation(id: string): Promise<Quotation | null> {
  const snap = await getDoc(doc(db, 'quotations', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Quotation, 'id'>) };
}

export async function fetchQuotationByToken(token: string): Promise<Quotation | null> {
  const snap = await getDocs(
    query(
      collection(db, 'quotations'),
      where('shareToken', '==', token),
      where('status', '==', 'final'),
      fbLimit(1),
    ),
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Omit<Quotation, 'id'>) };
}

export async function createQuotation(
  data: Omit<Quotation, 'id'>,
  uid?: string | null,
): Promise<{ id: string; quotationNumber: string }> {
  const ref = doc(collection(db, 'quotations'));
  const now = new Date();
  const datePart = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('');
  const quotationNumber = data.quotationNumber ?? `QT-${datePart}-${ref.id.slice(0, 5).toUpperCase()}`;

  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );
  await setDoc(ref, {
    ...cleanData,
    quotationNumber,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid ?? null,
  });
  return { id: ref.id, quotationNumber };
}

export async function updateQuotation(
  id: string,
  data: Partial<Quotation>,
): Promise<void> {
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );
  await updateDoc(doc(db, 'quotations', id), {
    ...cleanData,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteQuotation(id: string): Promise<void> {
  await deleteDoc(doc(db, 'quotations', id));
}
